import React, { useContext, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
  TabList,
  Tab,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Button,
  SelectTabEvent,
} from '@fluentui/react-components';
import {
  CalendarRegular,
  ContactCardRegular,
  DocumentCheckmarkRegular,
  DocumentTextRegular,
  FolderOpenRegular,
  InfoRegular,
  PeopleRegular,
  ServerRegular,
} from '@fluentui/react-icons';
import { mergeStyles } from '@fluentui/react';
import FullWidthHeader from './FullWidthHeader';
import { IPivotHeaderItems } from '../../../utils/types/IPivotHeaderItems';
import {
  pivotHeaderPivotMenu,
  pivotHeaderCommandButtonMenu,
} from '../../../app/common/styles/CommonStyleObjects';
import UserContext from '../../../utils/authorization/UserContext';
import hasAccess from '../../../utils/authorization/authorizationCheck';
import { StatUser } from '../../../utils/statApi/UsersApi';

const pivotHeaderIconMap: Record<string, () => JSX.Element> = {
  ServerProcesses: () => <ServerRegular aria-hidden />,
  Info: () => <InfoRegular aria-hidden />,
  OpenFolderHorizontal: () => <FolderOpenRegular aria-hidden />,
  Calendar: () => <CalendarRegular aria-hidden />,
  ContactCard: () => <ContactCardRegular aria-hidden />,
  TextDocument: () => <DocumentTextRegular aria-hidden />,
  People: () => <PeopleRegular aria-hidden />,
  ReviewSolid: () => <DocumentCheckmarkRegular aria-hidden />,
};

const getMenuIcon = (iconName?: string): JSX.Element | undefined => {
  if (!iconName) {
    return undefined;
  }

  const iconFactory = pivotHeaderIconMap[iconName];
  return iconFactory ? iconFactory() : undefined;
};

interface IPivotHeaderProps {
  baseUri: string;
  getHeaderTitle: () => JSX.Element;
  pivotHeaderItems: IPivotHeaderItems;
  ariaLabel: string;
  activeSegmentIndex?: number;
  defaultActiveSegment?: string;
  enableActiveTabPanelControls?: boolean;
}

const PivotHeader = (props: IPivotHeaderProps): JSX.Element => {
  const {
    baseUri,
    getHeaderTitle,
    pivotHeaderItems,
    ariaLabel,
    activeSegmentIndex,
    defaultActiveSegment,
    enableActiveTabPanelControls,
  } = props;

  const location = useLocation();
  const history = useHistory();
  const user = useContext(UserContext) as unknown as StatUser;

  // Filter out any tabs the current user does not have permission to view.
  // Tabs without a `requestedPermission` are always shown.
  const visibleHeaderItems = useMemo<IPivotHeaderItems>(() => {
    const filtered: IPivotHeaderItems = {};
    Object.keys(pivotHeaderItems ?? {}).forEach((key) => {
      const item = pivotHeaderItems[key];
      if (!item?.requestedPermission || hasAccess(user?.permissions, item.requestedPermission)) {
        filtered[key] = item;
      }
    });
    return filtered;
  }, [pivotHeaderItems, user?.permissions]);

  const uri = location.pathname;
  const pieces = uri.split('/');

  const rawSegment = activeSegmentIndex !== undefined ? pieces[activeSegmentIndex] : pieces[3];

  const segmentMap: Record<string, string> = {
    'dividends-review': 'fiscal-years',
    'agm-review': 'fiscal-years',
  };

  const activeBaseSegment = segmentMap[rawSegment] || rawSegment || defaultActiveSegment || '';

  const handleTabSelect = (_ev: SelectTabEvent, data: { value: string | number }) => {
    if (data?.value != null) {
      history.push(`${baseUri}/${data.value}`);
    }
  };

  // Mobile: menu item click → navigate
  const handleMobileSelect = (key: string) => {
    history.push(`${baseUri}/${key}`);
  };

  const renderSubtitleMenu = (): JSX.Element => {
    const keys = Object.keys(visibleHeaderItems ?? {});
    const tabListClass = mergeStyles(pivotHeaderPivotMenu.root);
    const menuButtonClass = mergeStyles(pivotHeaderCommandButtonMenu.root);

    return (
      <>
        <div className={tabListClass}>
          <TabList
            aria-label={ariaLabel}
            selectedValue={activeBaseSegment}
            onTabSelect={handleTabSelect}
          >
            {keys.map((key) => {
              const item = visibleHeaderItems[key];
              return (
                <Tab
                  key={key}
                  value={key}
                  id={`tab-${key}`}
                  aria-controls={
                    enableActiveTabPanelControls && key === activeBaseSegment
                      ? `panel-${key}`
                      : undefined
                  }
                >
                  {item?.headerText}
                </Tab>
              );
            })}
          </TabList>
        </div>
        <div className={menuButtonClass}>
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button aria-label="Select a section to navigate to">
                {visibleHeaderItems[activeBaseSegment]?.headerText ?? 'Select'}
              </Button>
            </MenuTrigger>

            <MenuPopover>
              <MenuList>
                {keys.map((key) => {
                  const item = visibleHeaderItems[key];
                  return (
                    <MenuItem
                      key={key}
                      icon={getMenuIcon(item.iconName)}
                      onClick={() => handleMobileSelect(key)}
                    >
                      {item?.headerText}
                    </MenuItem>
                  );
                })}
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
      </>
    );
  };

  return <FullWidthHeader title={getHeaderTitle} subtitle={renderSubtitleMenu} />;
};

export default PivotHeader;
